import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "./shared";

const MusicPlayerContext = createContext(null);

export const useMusicPlayer = () => useContext(MusicPlayerContext);

const PLAYER_ID = "union-music-yt-player";

export const MusicPlayerProvider = ({ children }) => {
  const [tracks, setTracks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [visible, setVisible] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [ytReady, setYtReady] = useState(false);
  const [ytError, setYtError] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const playerRef = useRef(null);

  const currentTrack = currentIdx >= 0 && currentIdx < tracks.length ? tracks[currentIdx] : null;

  // Load the YouTube IFrame API once
  useEffect(() => {
    if (window.YT && window.YT.Player) {
      setYtReady(true);
      return;
    }
    const existing = document.getElementById("yt-iframe-api");
    if (!existing) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      tag.id = "yt-iframe-api";
      document.body.appendChild(tag);
    }
    window.onYouTubeIframeAPIReady = () => setYtReady(true);
  }, []);

  // Load playlist from backend
  const loadPlaylist = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/music/playlist`);
      setTracks(data.tracks || []);
      setCategories(data.categories || []);
    } catch {
      // Silent — music is not critical
    }
  }, []);

  useEffect(() => { loadPlaylist(); }, [loadPlaylist]);

  // Create the YouTube player when API is ready and first track is loaded
  useEffect(() => {
    if (!ytReady || playerRef.current) return;
    const el = document.getElementById(PLAYER_ID);
    if (!el) return;
    try {
      playerRef.current = new window.YT.Player(PLAYER_ID, {
        height: "90",
        width: "160",
        playerVars: {
          playsinline: 1,
          controls: 0,
          rel: 0,
          modestbranding: 1,
        },
        events: {
          onReady: () => {},
          onStateChange: (e) => {
            const S = window.YT.PlayerState;
            if (e.data === S.PLAYING) setIsPlaying(true);
            else if (e.data === S.PAUSED) setIsPlaying(false);
            else if (e.data === S.ENDED) next();
          },
          onError: () => setYtError(true),
        },
      });
    } catch {
      setYtError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ytReady]);

  const play = useCallback((idx) => {
    const t = tracks[idx];
    if (!t) return;
    if (!t.video_id) {
      // Fall back to opening YouTube search
      window.open(t.youtube_search_url, "_blank", "noopener");
      toast("Opening YouTube search for this track in a new tab", { description: "Paste the YouTube URL into the Music Admin to play in-app next time." });
      return;
    }
    setCurrentIdx(idx);
    setVisible(true);
    setMinimized(false);
    if (playerRef.current?.loadVideoById) {
      try {
        playerRef.current.loadVideoById(t.video_id);
      } catch {
        toast.error("Couldn't start playback");
      }
    }
  }, [tracks]);

  const playByTrackId = useCallback((id) => {
    const i = tracks.findIndex((t) => t.id === id);
    if (i >= 0) play(i);
  }, [tracks, play]);

  const playYoutubeUrl = useCallback((url) => {
    const m = url?.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/) || url?.match(/^([A-Za-z0-9_-]{11})$/);
    const vid = m?.[1];
    if (!vid) {
      toast.error("That doesn't look like a valid YouTube link.");
      return;
    }
    const customTrack = { id: `custom-${vid}`, title: "Custom track", artist: "YouTube", category: "Custom", video_id: vid, thumbnail_url: `https://i.ytimg.com/vi/${vid}/hqdefault.jpg`, youtube_search_url: url };
    setTracks((prev) => {
      const without = prev.filter((t) => t.id !== customTrack.id);
      return [customTrack, ...without];
    });
    setTimeout(() => {
      // play after state updates
      if (playerRef.current?.loadVideoById) {
        setCurrentIdx(0);
        setVisible(true);
        setMinimized(false);
        try { playerRef.current.loadVideoById(vid); } catch {
          toast.error("Playback error");
        }
      }
    }, 50);
  }, []);

  const toggle = useCallback(() => {
    if (!playerRef.current) return;
    if (isPlaying) playerRef.current.pauseVideo();
    else playerRef.current.playVideo();
  }, [isPlaying]);

  const next = useCallback(() => {
    if (tracks.length === 0) return;
    if (shuffle) {
      // Pick a random playable track different from the current
      const playable = tracks.map((t, i) => ({ t, i })).filter((x) => x.t.video_id && x.i !== currentIdx);
      const pool = playable.length ? playable : tracks.map((t, i) => ({ t, i })).filter((x) => x.i !== currentIdx);
      if (!pool.length) return;
      const pick = pool[Math.floor(Math.random() * pool.length)];
      play(pick.i);
      return;
    }
    let i = currentIdx + 1;
    if (i >= tracks.length) i = 0;
    play(i);
  }, [currentIdx, tracks, play, shuffle]);

  const prev = useCallback(() => {
    if (tracks.length === 0) return;
    let i = currentIdx - 1;
    if (i < 0) i = tracks.length - 1;
    play(i);
  }, [currentIdx, tracks.length, play]);

  const shuffleAll = useCallback(() => {
    if (tracks.length === 0) return;
    setShuffle(true);
    const playable = tracks.map((t, i) => ({ t, i })).filter((x) => x.t.video_id);
    const pool = playable.length ? playable : tracks.map((t, i) => ({ t, i }));
    const pick = pool[Math.floor(Math.random() * pool.length)];
    play(pick.i);
  }, [tracks, play]);

  const closePlayer = useCallback(() => {
    if (playerRef.current?.stopVideo) try { playerRef.current.stopVideo(); } catch { /* ignore */ }
    setIsPlaying(false);
    setVisible(false);
  }, []);

  return (
    <MusicPlayerContext.Provider
      value={{
        tracks, categories, currentTrack, currentIdx, isPlaying, visible, minimized, ytReady, ytError, shuffle,
        play, playByTrackId, playYoutubeUrl, toggle, next, prev, closePlayer, shuffleAll, setShuffle,
        setMinimized, loadPlaylist,
      }}
    >
      {children}
      {/* Hidden YouTube player. The UI wraps this node. */}
      <div
        id={PLAYER_ID}
        style={{
          position: "fixed",
          bottom: -200,
          left: -200,
          width: 1,
          height: 1,
          pointerEvents: "none",
          opacity: 0,
        }}
      />
    </MusicPlayerContext.Provider>
  );
};
